/**
 * Explanation block. Portuguese first, English gloss second — always that
 * order, never the reverse (docs/01).
 */

import type { ExplanationPayload } from "../types.ts";
import { el, gloss, pt, ruleTil } from "../ui.ts";
import type { BlockContext, RenderedBlock } from "./types.ts";
import { primaryButton } from "../ui.ts";

export function renderExplanation(context: BlockContext): RenderedBlock {
	const payload = context.block.payload as unknown as ExplanationPayload;
	const ratio = context.englishRatio;
	const body: Node[] = [];

	body.push(el("h1", { class: "pt-lg", lang: "pt-BR", style: "font-size:25px", text: payload.title_pt }));
	const titleGloss = gloss(payload.title_en, ratio);
	if (titleGloss !== null) body.push(titleGloss);

	if (payload.body_pt.trim() !== "") {
		const prose = pt(payload.body_pt);
		prose.style.marginTop = "20px";
		body.push(prose);
		const bodyGloss = gloss(payload.gloss_en, ratio);
		if (bodyGloss !== null) body.push(bodyGloss);
	}

	if (payload.table_rows.length > 0) {
		body.push(ruleTil());
		body.push(conjugationTable(payload));
	}

	if (payload.examples.length > 0) {
		for (const example of payload.examples) {
			const line = pt(example.pt);
			line.style.marginTop = "20px";
			body.push(line);
			const exampleGloss = gloss(example.en, ratio);
			if (exampleGloss !== null) body.push(exampleGloss);
		}
	}

	const footer = primaryButton("Continue", () => {
		context.finish({ attempts: [] });
	});

	return { body, footer };
}

function conjugationTable(payload: ExplanationPayload): HTMLElement {
	const card = el("div", { class: "card" });
	if (payload.table_caption.trim() !== "") {
		card.append(el("div", { class: "meta", style: "margin-bottom:2px", lang: "pt-BR", text: payload.table_caption }));
	}

	const table = el("table", { class: "conj" });
	let anyIrregular = false;
	for (const row of payload.table_rows) {
		if (row.irregular) anyIrregular = true;
		const form = el("td", { class: "conj__form", lang: "pt-BR" }, row.form);
		if (row.irregular) {
			form.append(el("span", { class: "dot", "aria-label": "irregular form" }));
		}
		table.append(el("tr", {}, el("td", { class: "conj__person", lang: "pt-BR", text: row.person }), form));
	}
	card.append(table);

	if (anyIrregular) {
		card.append(
			el(
				"p",
				{ class: "en", style: "margin-top:12px" },
				el("span", { class: "dot", style: "margin:0 6px 0 0" }),
				"Marked forms are irregular or ambiguous — context tells you which.",
			),
		);
	}

	return card;
}
